import {
  Card,
  CardBody,
  CardFooter,
  Col,
  Row,
  Label,
  FormGroup,
} from "reactstrap";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Btn } from "../../AbstractElements";
import { useSweetAlert } from "../../Context/SweetAlertContext";
import { RegistrationService } from "../../Service/Registration/RegistrationService";
import { RegisterValidSchema } from "../../Forms/FormsVailidationSchema";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, useRef } from "react";

const RegistrationTabs = () => {
  const { registerUser, checkUser, loading } = RegistrationService();
  const { ShowSuccessAlert, showAlert } = useSweetAlert();

  const navigate = useNavigate();
  const location = useLocation();

  const [isSponsorValid, setIsSponsorValid] = useState(true);

  // ───────────────── PIN STATE ─────────────────
  const [pinProductId, setPinProductId] = useState<number | null>(null);
  const [pinChecking, setPinChecking] = useState(false);

  // OTP only for ProductId = 1
  const showOtpSection = pinProductId === 1;

  // ───────────────── OTP STATE ─────────────────
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [timerDisplay, setTimerDisplay] = useState("");
  const [canResend, setCanResend] = useState(false);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const defaultInitialValues = {
    SponsorUserName: "",
    SponsorName: "",
    PIN: "",
    FirstName: "",
    Gender: "",
    MobileNo: "",
    OTP: "",
    EmailId: "",
    Password: "",
    ConfirmPassword: "",
    AcceptTerms: false,
  };

  const [initialValues, setInitialValues] = useState(defaultInitialValues);

  // ───────────────── INITIAL LOAD ─────────────────
  useEffect(() => {
    const sponsorId =
      localStorage.getItem("UserName") ||
      localStorage.getItem("UID") ||
      "";

    const sponsorName =
      localStorage.getItem("MemberName") || "";

    const pinFromState = location.state?.pin;
    const pinFromStorage = localStorage.getItem("selectedPin");

    const selectedPin = pinFromState || pinFromStorage || "";

    setInitialValues((prev) => ({
      ...prev,
      SponsorUserName: sponsorId,
      SponsorName: sponsorName,
      PIN: selectedPin,
    }));

    if (selectedPin) {
      checkPin(selectedPin);
    }
  }, [location.state]);

  // ───────────────── CLEANUP ─────────────────
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // ───────────────── RESET OTP ─────────────────
  const resetOtpStates = (
    setFieldValue?: (field: string, value: any) => void
  ) => {
    setOtpSent(false);
    setOtpLoading(false);
    setTimerDisplay("");
    setCanResend(false);

    if (setFieldValue) {
      setFieldValue("OTP", "");
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  };

  // ───────────────── CHECK PIN ─────────────────
  const checkPin = async (
    pin: string,
    setFieldValue?: (field: string, value: any) => void
  ) => {
    if (!pin || !pin.trim()) {
      setPinProductId(null);
      resetOtpStates(setFieldValue);
      return;
    }

    setPinChecking(true);

    try {
      const obj = {
        procName: "CheckPinDetail",
        Para: JSON.stringify({
          PIN: pin.trim(),
        }),
      };

      const res = await checkUser(obj);

      if (res && res.length > 0) {
        const data = res[0];

        if (data.StatusCode === 1) {
          const productId = Number(data.ProductId);

          setPinProductId(productId);

          // reset OTP when pin changes
          resetOtpStates(setFieldValue);

          // remove OTP value if product is not 1
          if (productId !== 1 && setFieldValue) {
            setFieldValue("OTP", "");
          }
        } else {
          setPinProductId(null);
          resetOtpStates(setFieldValue);
        }
      } else {
        setPinProductId(null);
        resetOtpStates(setFieldValue);
      }
    } catch (error) {
      console.error("PIN Check Error :", error);
      setPinProductId(null);
      resetOtpStates(setFieldValue);
    } finally {
      setPinChecking(false);
    }
  };

  // ───────────────── CHECK SPONSOR ─────────────────
  const checkSponsor = async (
    sponsorId: string,
    setFieldValue: (field: string, value: any) => void,
    setValid: (v: boolean) => void
  ) => {
    if (!sponsorId.trim()) return;

    try {
      const obj = {
        procName: "CheckSponsor",
        Para: JSON.stringify({
          UserName: sponsorId,
        }),
      };

      const res = await checkUser(obj);

      if (res && res.length > 0) {
        const data = res[0];

        if (data.StatusCode === 1) {
          setFieldValue("SponsorName", data.Name);
          setValid(true);
        } else {
          setFieldValue("SponsorName", data.Msg);
          setValid(false);
        }
      } else {
        setFieldValue("SponsorName", "No record found");
        setValid(false);
      }
    } catch (error) {
      console.error(error);
      setFieldValue("SponsorName", "Error fetching sponsor");
      setValid(false);
    }
  };

  // ───────────────── TIMER ─────────────────
  const startTimer = (secondsLeft: number) => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    setCanResend(false);

    let remaining = secondsLeft;

    const formatTime = (sec: number) => {
      const min = Math.floor(sec / 60);
      const seconds = sec % 60;

      return `${String(min).padStart(2, "0")}:${String(
        seconds
      ).padStart(2, "0")}`;
    };

    setTimerDisplay(formatTime(remaining));

    timerRef.current = setInterval(() => {
      remaining--;

      if (remaining <= 0) {
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }

        setTimerDisplay("");
        setCanResend(true);
      } else {
        setTimerDisplay(formatTime(remaining));
      }
    }, 1000);
  };

  // ───────────────── SEND OTP ─────────────────
  const handleSendOTP = async (
    mobileNo: string,
    setFieldValue: (field: string, value: any) => void
  ) => {
    if (!mobileNo || mobileNo.length < 10) {
      showAlert(
        "Error",
        "Please enter valid mobile number first."
      );
      return;
    }

    setOtpLoading(true);

    try {
      const obj = {
        procName: "RegistrationOTP",
        Para: JSON.stringify({
          Mobilenumber: mobileNo,
          ActionMode: "SendOTPSMS",
        }),
      };

      const res = await checkUser(obj);

      if (res && res.length > 0) {
        const data = res[0];

        if (data.StatusCode === 1) {
          setOtpSent(true);

          setFieldValue("OTP", "");

          startTimer(data.SecondsLeft || 300);
        } else {
          showAlert("Error", data.Msg || "Failed to send OTP");
        }
      } else {
        showAlert("Error", "No response from server");
      }
    } catch (error) {
      console.error(error);
      showAlert("Error", "Something went wrong");
    } finally {
      setOtpLoading(false);
    }
  };

  // ───────────────── MOBILE CHANGE ─────────────────
  const handleMobileChange = (
    value: string,
    setFieldValue: (field: string, value: any) => void
  ) => {
    setFieldValue("MobileNo", value);

    resetOtpStates(setFieldValue);
  };

  // ───────────────── SUBMIT ─────────────────
  const handleSubmit = async (
    values: typeof defaultInitialValues
  ) => {
    try {
      // hard validation for OTP
      if (showOtpSection) {
        if (!values.OTP || values.OTP.trim() === "") {
          showAlert("Error", "OTP is required");
          return;
        }
      }

      const {
        ConfirmPassword,
        SponsorName,
        AcceptTerms,
        ...rest
      } = values;

      // remove OTP if not required
      const payload =
        showOtpSection
          ? rest
          : {
              ...rest,
              OTP: "",
            };

      const resArr = await registerUser(payload);

      const res = Array.isArray(resArr)
        ? resArr[0]
        : resArr;

      if (res?.StatusCode == 1) {
        ShowSuccessAlert(res.Msg);

        navigate(
          `${import.meta.env.BASE_URL}/thankyouregister`,
          {
            state: {
              userName: res.Name,
              userId: res.UserId,
              password: res.Password,
            },
          }
        );
      } else {
        showAlert(
          "Oops!",
          res?.Msg || "Registration Failed"
        );
      }
    } catch (error) {
      console.error(error);

      showAlert(
        "Error",
        "An unexpected error occurred."
      );
    }
  };

  // ───────────────── VALIDATION ─────────────────
  const validationSchema = RegisterValidSchema.concat(
    Yup.object().shape({
      OTP: Yup.string().when([], {
        is: () => showOtpSection,
        then: (schema) =>
          schema
            .required("OTP is required")
            .min(4, "Enter valid OTP"),
        otherwise: (schema) => schema.notRequired(),
      }),
    })
  );

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({
        isSubmitting,
        setFieldValue,
        values,
      }) => (
        <Form>
          <Card className="shadow-none">
            <CardBody>
              <Row>

                {/* Sponsor ID */}
                <Col sm="6" md="6">
                  <FormGroup>
                    <Label>Sponsor Id *</Label>

                    <Field
                      type="text"
                      name="SponsorUserName"
                      className="form-control"
                      placeholder="Enter Sponsor Id"
                      onKeyUp={(
                        e: React.KeyboardEvent<HTMLInputElement>
                      ) =>
                        checkSponsor(
                          (e.target as HTMLInputElement).value,
                          setFieldValue,
                          setIsSponsorValid
                        )
                      }
                    />

                    <ErrorMessage
                      name="SponsorUserName"
                      component="div"
                      className="text-danger"
                    />
                  </FormGroup>
                </Col>

                {/* Sponsor Name */}
                <Col sm="6" md="6">
                  <FormGroup>
                    <Label>Sponsor Name *</Label>

                    <Field
                      type="text"
                      name="SponsorName"
                      className="form-control"
                      disabled
                    />
                  </FormGroup>
                </Col>

                {/* PIN */}
                <Col sm="6" md="6">
                  <FormGroup>
                    <Label>
                      PIN *

                      {pinChecking && (
                        <span
                          className="spinner-border spinner-border-sm text-secondary ms-1"
                          role="status"
                        />
                      )}
                    </Label>

                    <Field name="PIN">
                      {({ field }: any) => (
                        <input
                          {...field}
                          type="text"
                          className="form-control"
                          placeholder="Enter PIN"
                          onChange={async (
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            const value = e.target.value;

                            setFieldValue("PIN", value);

                            await checkPin(
                              value,
                              setFieldValue
                            );
                          }}
                        />
                      )}
                    </Field>

                    <ErrorMessage
                      name="PIN"
                      component="div"
                      className="text-danger"
                    />
                  </FormGroup>
                </Col>

                {/* Full Name */}
                <Col sm="6" md="6">
                  <FormGroup>
                    <Label>Full Name *</Label>

                    <Field
                      type="text"
                      name="FirstName"
                      className="form-control"
                      placeholder="Enter Full Name"
                    />

                    <ErrorMessage
                      name="FirstName"
                      component="div"
                      className="text-danger"
                    />
                  </FormGroup>
                </Col>

                {/* Mobile */}
                <Col sm="6" md="6">
                  <FormGroup>
                    <Label>Mobile Number *</Label>

                    <Field
                      type="text"
                      name="MobileNo"
                      className="form-control"
                      placeholder="Enter Mobile Number"
                      onChange={(
                        e: React.ChangeEvent<HTMLInputElement>
                      ) =>
                        handleMobileChange(
                          e.target.value,
                          setFieldValue
                        )
                      }
                    />

                    <ErrorMessage
                      name="MobileNo"
                      component="div"
                      className="text-danger"
                    />
                  </FormGroup>
                </Col>

                {/* OTP SECTION */}
                {showOtpSection && (
                  <Col sm="6" md="6">
                    <FormGroup>
                      <Label>OTP *</Label>

                      <div className="d-flex gap-2">
                        <Field
                          type="text"
                          name="OTP"
                          className="form-control"
                          placeholder="Enter OTP"
                          maxLength={6}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) =>
                            setFieldValue(
                              "OTP",
                              e.target.value.replace(/\D/g, "")
                            )
                          }
                        />

                        <button
                          type="button"
                          className={`btn text-nowrap ${
                            canResend
                              ? "btn-warning"
                              : "btn-outline-primary"
                          }`}
                          style={{ minWidth: "110px" }}
                          disabled={
                            otpLoading ||
                            (otpSent && !canResend)
                          }
                          onClick={() =>
                            handleSendOTP(
                              values.MobileNo,
                              setFieldValue
                            )
                          }
                        >
                          {otpLoading ? (
                            <span
                              className="spinner-border spinner-border-sm"
                              role="status"
                            />
                          ) : canResend ? (
                            <>
                              <i className="fa fa-refresh me-1" />
                              Resend
                            </>
                          ) : (
                            <>
                              <i className="fa fa-paper-plane me-1" />
                              Send OTP
                            </>
                          )}
                        </button>
                      </div>

                      {otpSent &&
                        timerDisplay &&
                        !canResend && (
                          <small className="text-muted mt-1 d-block">
                            OTP sent to entered mobile.
                            Expires in{" "}
                            <span className="text-danger fw-bold">
                              {timerDisplay}
                            </span>
                          </small>
                        )}

                      {canResend && (
                        <small className="text-warning mt-1 d-block">
                          OTP expired. Click Resend to
                          get a new OTP.
                        </small>
                      )}

                      <ErrorMessage
                        name="OTP"
                        component="div"
                        className="text-danger"
                      />
                    </FormGroup>
                  </Col>
                )}

                {/* Email */}
                <Col sm="6" md="6">
                  <FormGroup>
                    <Label>Email Id</Label>

                    <Field
                      type="email"
                      name="EmailId"
                      className="form-control"
                      placeholder="Enter Email"
                    />
                  </FormGroup>
                </Col>

                {/* Password */}
                <Col sm="6" md="6">
                  <FormGroup>
                    <Label>Password *</Label>

                    <Field
                      type="password"
                      name="Password"
                      className="form-control"
                      placeholder="Enter Password"
                    />

                    <ErrorMessage
                      name="Password"
                      component="div"
                      className="text-danger"
                    />
                  </FormGroup>
                </Col>

                {/* Confirm Password */}
                <Col sm="6" md="6">
                  <FormGroup>
                    <Label>Confirm Password *</Label>

                    <Field
                      type="password"
                      name="ConfirmPassword"
                      className="form-control"
                      placeholder="Re-enter Password"
                    />

                    <ErrorMessage
                      name="ConfirmPassword"
                      component="div"
                      className="text-danger"
                    />
                  </FormGroup>
                </Col>

                {/* Gender */}
                <Col sm="6" md="6">
                  <FormGroup>
                    <Label>Gender *</Label>

                    <Field
                      as="select"
                      name="Gender"
                      className="btn-square form-select form-control"
                    >
                      <option value="">
                        Select Gender
                      </option>
                      <option value="Male">
                        Male
                      </option>
                      <option value="Female">
                        Female
                      </option>
                    </Field>

                    <ErrorMessage
                      name="Gender"
                      component="div"
                      className="text-danger"
                    />
                  </FormGroup>
                </Col>

                {/* Terms */}
                <Col sm="12">
                  <FormGroup check>
                    <Label check>
                      <Field
                        type="checkbox"
                        name="AcceptTerms"
                        className="me-2"
                      />

                      I agree to the Terms &
                      Conditions of Sukanya
                      Swastik Care.
                    </Label>

                    <ErrorMessage
                      name="AcceptTerms"
                      component="div"
                      className="text-danger"
                    />
                  </FormGroup>
                </Col>

              </Row>
            </CardBody>

            <CardFooter className="text-end">
              <Btn
                color="primary"
                type="submit"
                disabled={
                  isSubmitting ||
                  loading ||
                  !isSponsorValid ||
                  pinChecking
                }
              >
                Register

                {loading && (
                  <div
                    className="spinner-border spinner-border-sm text-dark ms-2"
                    role="status"
                  >
                    <span className="sr-only">
                      Loading...
                    </span>
                  </div>
                )}
              </Btn>
            </CardFooter>
          </Card>
        </Form>
      )}
    </Formik>
  );
};

export default RegistrationTabs;